<template>
    <div
        class="bo-confirmation bo-modal-layer"
        data-bo-modal-layer
        :style="{ zIndex: layerZIndex }"
    >
        <div
            v-if="backdropVisible"
            class="bo-confirmation-backdrop fade"
            :class="{ show: modalActive }"
            aria-hidden="true"
        ></div>
        <div
            class="modal bo-modal fade"
            :class="[modalColour, { show: modalActive }]"
            :id="modalId"
            :style="{ display: modalVisible ? 'block' : 'none' }"
            tabindex="-1"
            role="dialog"
            :aria-labelledby="modalTitleId"
            :aria-hidden="modalVisible ? 'false' : 'true'"
            :aria-modal="modalVisible ? 'true' : null"
            @mousedown.self="_backdropClick"
            @click.self="_backdropClick"
        >
            <div
                class="modal-dialog modal-dialog-centered"
                role="document"
                @mousedown.self="_backdropClick"
                @click.self="_backdropClick"
            >
                <div class="modal-content">
                    <div class="modal-header">
                        <div class="left-panel">
                            <div
                                :id="modalTitleId"
                                class="modal-title"
                            >
                                {{ modalDisplayTitle }}
                            </div>
                        </div>
                        <button
                            v-if="closeable"
                            type="button"
                            class="close"
                            aria-label="Close"
                            @click="_dismiss"
                        >
                            <i class="icon-close"></i>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div
                            v-if="body && showAsVHtml"
                            v-html="body"
                            class="text-break"
                        ></div>
                        <div
                            v-if="body && !showAsVHtml"
                            class="text-break"
                        >
                            {{ body }}
                        </div>
                        <div
                            class="text-break"
                            v-else
                        >
                            <slot name="body"></slot>
                        </div>
                    </div>
                    <div
                        class="modal-footer"
                        v-if="showFooter"
                    >
                        <button
                            id="cancel_button"
                            v-if="showCancelButton"
                            type="button"
                            class="btn btn-rounded light"
                            @click="_cancel"
                        >
                            {{ cancelLabel ?? cancelString }}
                        </button>
                        <button
                            id="confirm_button"
                            type="button"
                            class="btn btn-rounded action"
                            :class="confirmButtonColour"
                            @click="_confirm"
                            v-if="showConfirmButton"
                        >
                            {{ confirmationText }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import modalLayerManager from "../services/modal-layer-manager";

export default {
    props: {
        id: {
            type: String,
            default: null,
        },
        cancel: {
            type: Boolean,
            default: null,
        },
        confirm: {
            type: Boolean,
            default: null,
        },
        confirmColor: {
            type: String,
            default: null,
        },
        confirmText: {
            type: String,
            default: null,
        },
        cancelColor: {
            type: String,
            default: null,
        },
        title: {
            type: String,
            default: null,
        },
        closeable: {
            type: Boolean,
            default: true,
        },
        customModalBackdrop: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            generatedModalId: this.id ?? `bo-confirmation_${uuidv4()}`,
            modalTitle: "",
            body: null,
            action: null,
            colour: null,
            resolvePromise: undefined,
            onConfirmCallback: null,
            canCancel: true,
            toConfirm: false,
            backdropVisible: false,
            modalVisible: false,
            modalActive: false,
            modalLayer: null,
            layerZIndex: null,
            isClosing: false,
            closeTransitionCleanup: null,
            showFooter: true,
            showAsVHtml: true,
            cancelString: window.bo?.generalTranslations?.cancel ?? "Cancel",
            cancelLabel: null,
            dismissResult: false,
        };
    },

    computed: {
        modalColour: {
            get() {
                switch (this.confirmColor ?? this.colour) {
                    case "info":
                        return "info";
                    case "warning":
                        return "danger";
                    default:
                        return "info";
                }
            },
        },
        confirmButtonColour: {
            get() {
                switch (this.confirmColor ?? this.colour) {
                    case "warning":
                        return "danger";
                    default:
                        return "arclight";
                }
            },
        },
        showCancelButton: {
            get() {
                return this.cancel ?? this.canCancel;
            },
        },
        showConfirmButton: {
            get() {
                return this.confirm ?? this.confirmationText !== null;
            },
        },
        confirmationText: {
            get() {
                return this.confirmText ?? this.action;
            },
        },
        modalId: {
            get() {
                return this.id ?? this.generatedModalId;
            },
        },
        modalTitleId: {
            get() {
                return `${this.modalId}_title`;
            },
        },
        modalDisplayTitle: {
            get() {
                return this.title ?? this.modalTitle;
            },
        },
    },

    mounted() {
        if (this.$el && this.$el.parentNode && this.$el.parentNode !== document.body) {
            document.body.appendChild(this.$el);
        }

        this._onDocumentKeydown = this._handleDocumentKeydown.bind(this);
        this._onDocumentFocusIn = this._handleDocumentFocusIn.bind(this);
    },

    beforeDestroy() {
        this._removeDocumentListeners();
        this._clearCloseTransition();
        modalLayerManager.release(this.modalLayer);
        this.modalLayer = null;
        this.backdropVisible = false;
        this.modalVisible = false;

        if (this.$el && this.$el.parentNode === document.body) {
            document.body.removeChild(this.$el);
        }
    },

    methods: {
        // cancelText relabels Cancel; distinguishDismiss resolves null (not false) on X/backdrop/Escape.
        // onConfirm runs synchronously in the click that answers the prompt: callers that must stay
        // inside that user gesture (opening a file dialog) cannot use the returned promise, which
        // resolves only after the close transition.
        activate(
            title,
            body,
            action = null,
            colour = null,
            cancel = null,
            footer = true,
            v_html = true,
            cancelText = null,
            distinguishDismiss = false,
            onConfirm = null
        ) {
            this.onConfirmCallback = onConfirm;
            this.cancelLabel = cancelText;
            this.dismissResult = distinguishDismiss ? null : false;
            this.modalTitle = title;
            this.body = body;
            this.action = action;
            this.colour = colour;
            this.canCancel = cancel;
            this.showFooter = footer;
            this.showAsVHtml = v_html;

            return new Promise((resolve) => {
                this.resolvePromise = resolve;
                this.openModal();
            });
        },

        activateStatic() {
            this.cancelLabel = null;
            this.dismissResult = false;
            return new Promise((resolve) => {
                this.resolvePromise = resolve;
                this.openModal();
            });
        },

        _confirm() {
            this.toConfirm = true;
            // close first: closeModal() drops modalActive synchronously, so the focus trap stops
            // pulling focus back into the modal while the callback below hands focus elsewhere
            // (the file input). The callback still runs in this click, so it keeps the user gesture.
            this.closeModal();
            this.runOnConfirm();
        },

        // one-shot, so a second click cannot fire it twice
        runOnConfirm() {
            const onConfirm = this.onConfirmCallback;
            this.onConfirmCallback = null;

            if (onConfirm) {
                onConfirm();
            }
        },

        _cancel() {
            this.toConfirm = false;
            this.closeModal();
        },

        _dismiss() {
            this.toConfirm = this.dismissResult;
            this.closeModal();
        },

        _backdropClick() {
            if (this.closeable) {
                this._dismiss();
            }
        },

        openModal() {
            if (this.modalVisible || this.isClosing) return;

            const modalEl = this.$el.querySelector(".modal") || document.getElementById(this.modalId);
            this.modalLayer = modalLayerManager.acquire(this.$el);
            this.layerZIndex = this.modalLayer.zIndex;
            this.backdropVisible = true;
            this.modalVisible = true;
            this.modalActive = false;
            document.addEventListener("keydown", this._onDocumentKeydown);
            document.addEventListener("focusin", this._onDocumentFocusIn);

            this.$nextTick(() => {
                modalLayerManager.setSurface(this.modalLayer, modalEl);
                void modalEl.offsetHeight;
                this.modalActive = true;

                this.$nextTick(() => {
                    this._focusInitialElement();
                });
            });
        },

        closeModal() {
            if (!this.modalVisible || this.isClosing) return;

            this.isClosing = true;
            modalLayerManager.beginClose(this.modalLayer);
            this.modalActive = false;

            this.$nextTick(() => {
                this._waitForCloseTransition();
            });
        },

        _waitForCloseTransition() {
            const dialogElement = this.$el.querySelector(".modal-dialog");
            if (!dialogElement) {
                this._completeClose();
                return;
            }

            const computedStyle = window.getComputedStyle(dialogElement);
            const durations = computedStyle.transitionDuration.split(",");
            const delays = computedStyle.transitionDelay.split(",");
            const toMilliseconds = (value) => {
                const parsedValue = Number.parseFloat(value);
                return value.trim().endsWith("ms") ? parsedValue : parsedValue * 1000;
            };
            const transitionDuration = durations.reduce((longestDuration, duration, index) => {
                const delay = delays[index] ?? delays[0] ?? "0s";
                return Math.max(longestDuration, toMilliseconds(duration) + toMilliseconds(delay));
            }, 0);

            if (transitionDuration === 0) {
                this._completeClose();
                return;
            }

            let completed = false;
            const complete = () => {
                if (completed) return;
                completed = true;
                this._clearCloseTransition();
                this._completeClose();
            };
            const onTransitionEnd = (event) => {
                if (event.target === dialogElement) complete();
            };
            const timeoutId = window.setTimeout(complete, transitionDuration + 50);

            dialogElement.addEventListener("transitionend", onTransitionEnd);
            this.closeTransitionCleanup = () => {
                window.clearTimeout(timeoutId);
                dialogElement.removeEventListener("transitionend", onTransitionEnd);
                this.closeTransitionCleanup = null;
            };
        },

        _clearCloseTransition() {
            if (this.closeTransitionCleanup) this.closeTransitionCleanup();
        },

        _completeClose() {
            this._removeDocumentListeners();
            this.modalVisible = false;
            this.backdropVisible = false;
            const closingLayer = this.modalLayer;
            const confirmationResult = this.toConfirm;

            this.$nextTick(() => {
                modalLayerManager.release(closingLayer);
                if (this.modalLayer === closingLayer) {
                    this.modalLayer = null;
                    this.layerZIndex = null;
                }
                this.isClosing = false;

                if (this.resolvePromise) {
                    this.resolvePromise(confirmationResult);
                    this.resolvePromise = undefined;
                }
                this.toConfirm = false;
                // cancelled or dismissed: never let a stale callback reach the next activation
                this.onConfirmCallback = null;
            });
        },

        _handleDocumentKeydown(event) {
            if (!modalLayerManager.isTop(this.modalLayer)) return;

            if (
                event.key === "Escape"
                && this.closeable
            ) {
                event.preventDefault();
                this._dismiss();
                return;
            }

            if (event.key !== "Tab") return;

            const modalEl = this.$el.querySelector(".modal");
            if (!modalEl) return;

            const focusableElements = this._getFocusableElements(modalEl);
            if (focusableElements.length === 0) {
                event.preventDefault();
                modalEl.focus();
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        },

        _handleDocumentFocusIn(event) {
            if (!this.modalActive || !modalLayerManager.isTop(this.modalLayer)) return;

            const modalEl = this.$el.querySelector(".modal");
            if (modalEl && !modalEl.contains(event.target)) this._focusInitialElement();
        },

        _focusInitialElement() {
            if (!this.modalActive || !modalLayerManager.isTop(this.modalLayer)) return;

            const modalEl = this.$el.querySelector(".modal");
            if (!modalEl) return;

            const focusableElement = this._getFocusableElements(modalEl)[0];
            (focusableElement ?? modalEl).focus();
        },

        _getFocusableElements(modalEl) {
            return Array.from(modalEl.querySelectorAll(
                "[autofocus], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
            )).filter((element) => element.getClientRects().length > 0);
        },

        _removeDocumentListeners() {
            document.removeEventListener("keydown", this._onDocumentKeydown);
            document.removeEventListener("focusin", this._onDocumentFocusIn);
        },
    },
};
</script>
